import { api } from './authService';

export interface Tenant {
  id: number;
  code: string;
  name: string;
  description?: string;
  status: string;
  created_at: string;
  updated_at: string;
  language?: string;
  timezone?: string;
  theme?: string;
  eroad_tenant_id?: string;
}

export interface User {
  id: number;
  username: string;
  is_super_admin?: boolean;
  tenant_code?: string;
  created_at: string;
}

export interface TenantListResponse {
  success: boolean;
  data: Tenant[];
  message?: string;
}

export interface UserListResponse {
  success: boolean;
  data: User[];
  message?: string;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  isSuperAdmin?: boolean;
  tenant_code?: string;
  tenants?: Array<{
    tenant_id: number;
    role?: string;
    is_tenant_admin?: boolean;
  }>;
}

export interface CreateTenantRequest {
  code: string;
  name: string;
  description?: string;
  status?: string;
  language?: string;
  timezone?: string;
  theme?: string;
  eroad_tenant_id?: string;
}

export interface UpdateTenantRequest {
  name?: string;
  description?: string;
  status?: string;
  language?: string;
  timezone?: string;
  theme?: string;
  eroad_tenant_id?: string;
}

class TenantService {
  /**
   * 获取所有租户列表
   */
  async getAllTenants(): Promise<Tenant[]> {
    try {
      const response = await api.get<TenantListResponse>('/tenants');
      if (response.data.success) {
        return response.data.data || [];
      }
      throw new Error(response.data.message || '获取租户列表失败');
    } catch (error: any) {
      console.error('Failed to fetch tenants:', error);
      throw error;
    }
  }

  /**
   * 获取当前用户的租户列表
   */
  async getUserTenants(): Promise<Tenant[]> {
    try {
      const response = await api.get<TenantListResponse>('/tenants/user');
      if (response.data.success) {
        return response.data.data || [];
      }
      throw new Error(response.data.message || '获取用户租户列表失败');
    } catch (error: any) {
      console.error('Failed to fetch user tenants:', error);
      throw error;
    }
  }

  /**
   * 获取所有用户列表
   */
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await api.get<UserListResponse>('/tenants/users/all');
      if (response.data.success) {
        return response.data.data || [];
      }
      throw new Error(response.data.message || '获取用户列表失败');
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      throw error;
    }
  }

  /**
   * 重置用户密码
   */
  async resetUserPassword(userId: number, newPassword: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.post(`/tenants/users/${userId}/reset-password`, {
        newPassword
      });
      return { success: true, message: response.data.message };
    } catch (error: any) {
      console.error('Failed to reset user password:', error);
      throw error;
    }
  }

  /**
   * 更新用户信息
   */
  async updateUser(userId: number, updates: { username?: string; tenant_code?: string; isSuperAdmin?: boolean }): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.put(`/tenants/users/${userId}`, updates);
      return { success: true, message: response.data.message };
    } catch (error: any) {
      console.error('Failed to update user:', error);
      throw error;
    }
  }

  /**
   * 创建用户（需要超级管理员）
   */
  async createUser(payload: CreateUserRequest): Promise<any> {
    try {
      const response = await api.post('/tenants/users', payload);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create user:', error);
      throw error;
    }
  }

  /**
   * 创建租户（需要超级管理员）
   */
  async createTenant(payload: CreateTenantRequest): Promise<Tenant> {
    try {
      const response = await api.post('/tenants', payload);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || response.data.message || '创建租户失败');
    } catch (error: any) {
      console.error('Failed to create tenant:', error);
      throw error;
    }
  }

  /**
   * 更新租户（需要超级管理员）
   */
  async updateTenant(id: number, payload: UpdateTenantRequest): Promise<void> {
    try {
      const response = await api.put(`/tenants/${id}`, payload);
      if (!response.data.success) {
        throw new Error(response.data.error || response.data.message || '更新租户失败');
      }
    } catch (error: any) {
      console.error('Failed to update tenant:', error);
      throw error;
    }
  }

  /**
   * 删除租户（需要超级管理员）
   */
  async deleteTenant(id: number): Promise<void> {
    try {
      const response = await api.delete(`/tenants/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.error || response.data.message || '删除租户失败');
      }
    } catch (error: any) {
      console.error('Failed to delete tenant:', error);
      throw error;
    }
  }
}

export const tenantService = new TenantService();